import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { InventoryLogType } from '@getgreenline/inventory/build/models/inventoryLog'
import {
  IBaseProduct,
  IBatchResponseWithStatus,
  API,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../../../../utilities/helpers'
import { InventoryBadge } from './InventoryBadge'
import { InventoryDrawer } from './InventoryDrawer'
import { UpdateInventory } from './UpdateInventory'
import { message } from 'antd'
import '../InventoryRow.scss'
import { InventoryLogStore } from './InventoryLogStore'

interface Props {
  locationId: number
  currentCompanyStore?: CurrentCompanyStore
  product: IBaseProduct
  quantity: number
  lowStockQuantity: number | null
  restockQuantity: number | null
  showLowStockQuantity: boolean
  onUpdate: () => void
}

interface State {
  isOpen: boolean
  batches: IBatchResponseWithStatus[]
  inventoryLogStore: InventoryLogStore
}

@inject('currentCompanyStore')
@observer
export class InventoryDisplay extends React.Component<Props, State> {
  popoverId = ''

  updateInventory = new UpdateInventory()

  constructor(props: Props) {
    super(props)
    this.state = {
      isOpen: false,
      batches: [],
      inventoryLogStore: new InventoryLogStore(
        props.currentCompanyStore!.company!,
        props.product.id,
        props.locationId,
      ),
    }
  }

  async loadBatches() {
    const company = this.props.currentCompanyStore!.company!
    const product = this.props.product

    try {
      const batches = await API.getAvailableBatches(company.id, this.props.locationId, product.id)
      const openBatches = batches.filter((batch) => batch.status === 'open')

      this.setState({ batches: openBatches })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occured while retreiving lots information')
    }
  }

  toggle() {
    const { isOpen } = this.state
    if (!isOpen) {
      this.updateInventory.setMode(undefined)
      this.setState({ isOpen: true })
      if (this.state.batches.length === 0) {
        this.loadBatches()
      }
    }
  }

  toggleActionButtons = (action: undefined | 'set' | 'add' | 'subtract') => {
    if (this.updateInventory.mode === action) {
      this.updateInventory.setMode(undefined)
      return
    }
    this.updateInventory.setMode(action)
  }

  update = () => {
    const updateMode = this.updateInventory.mode
    if (!updateMode) {
      return
    }

    const product = this.props.product
    if (product.isBatchTracked && !this.updateInventory.batchId) {
      return
    }

    const quantity = this.updateInventory.quantity

    let batches
    if (this.updateInventory.batchId !== undefined) {
      batches = [
        {
          batchId: this.updateInventory.batchId,
          quantity: quantity || 0,
        },
      ]
    }

    return this.props
      .currentCompanyStore!.updateBulkInventory(this.props.locationId, {
        mode: updateMode === 'set' ? 'set' : 'add',
        updates: [
          {
            productId: product.id,
            quantity: quantity || 0,
            lowStockQuantity: this.props.lowStockQuantity,
            type: this.updateInventory.type!,
            notes: this.updateInventory.notes,
            batchInfo: {
              isBatchTracked: product.isBatchTracked,
              batches: batches,
            },
          },
        ],
      })
      .then(async (updatedBulkInventory) => {
        message.success('Quantity has been updated', 2)
        this.updateInventory.setMostRecentQuantity(
          updatedBulkInventory[0].newQuantity || updatedBulkInventory[0].quantity,
        )
        await this.loadBatches()
        await this.state.inventoryLogStore.getInventoryLogs()
        await this.state.inventoryLogStore.getInventoryGraphLogs()
        this.updateInventory.setMode(undefined) // deselect current mode
      })
      .catch((error) => {
        message.error(parseErrorMsg(error || 'An error occurred while updating quantity'))
      })
  }

  isValidNumber(x: number | undefined | null): boolean {
    return x !== undefined && x !== null && !isNaN(x)
  }

  getNumberOrDefault(x: number | undefined | null, y: number | undefined) {
    return this.isValidNumber(x) ? (x as number) : y
  }

  get mostRecentLowStockQuantity() {
    const fallbackQuantity =
      this.props.lowStockQuantity === null ? undefined : this.props.lowStockQuantity

    return this.getNumberOrDefault(this.updateInventory.newLowStockQuantity, fallbackQuantity)
  }

  get mostRecentReStockQuantity() {
    const fallbackQuantity =
      this.props.restockQuantity === null ? undefined : this.props.restockQuantity

    return this.getNumberOrDefault(this.updateInventory.newReStockQuantity, fallbackQuantity)
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const { product, quantity, locationId, showLowStockQuantity } = this.props
    const locations = this.props.currentCompanyStore!.locations
    const { isOpen } = this.state
    const mostRecentQuantity = this.getNumberOrDefault(
      this.updateInventory.mostRecentQuantity,
      quantity,
    )
    const selectReason = {
      value: this.updateInventory.type,
      options: this.updateInventory.availableInventoryTypes,
      onChange: (value: InventoryLogType) => {
        this.updateInventory.setType(value)
      },
    }

    if (!locations) {
      return <span />
    }

    const location = locations.find((locationEntry) => locationEntry.id === locationId)!

    this.popoverId = `popover-${product.id}-${locationId}`

    let className = 'inventory-row-quantity '
    const lowStockQuantity = this.mostRecentLowStockQuantity

    if (lowStockQuantity) {
      if (quantity <= 0) {
        className += 'danger'
      } else if (quantity > 0 && quantity <= lowStockQuantity) {
        className += 'warning'
      } else {
        className += 'success'
      }
    } else {
      if (quantity > 0) {
        className += 'success'
      } else if (quantity < 0) {
        className += 'danger'
      }
    }

    return (
      <td
        id='inventory-log-td'
        key={locationId}
        onClick={() => this.toggle()}
        style={{ width: '5em', whiteSpace: 'nowrap', cursor: 'pointer' }}
        className={className}
      >
        <div>
          <div className='text-right'>
            <InventoryBadge
              id={this.popoverId}
              quantity={mostRecentQuantity || 0}
              lowStockQuantity={this.mostRecentLowStockQuantity}
              showLowStockQuantity={showLowStockQuantity}
              onClick={this.toggle}
            />
          </div>
          {isOpen && (
            <InventoryDrawer
              product={product}
              batches={this.state.batches}
              selectedBatchId={this.updateInventory.batchId}
              quantity={mostRecentQuantity || 0}
              toggleActionButtons={this.toggleActionButtons}
              onApply={this.update}
              mode={this.updateInventory.mode}
              locationId={location.id}
              companyId={company.id}
              lowStockQuantity={this.mostRecentLowStockQuantity}
              reStockQuantity={this.mostRecentReStockQuantity}
              selectReason={selectReason}
              quantitySetTo={this.updateInventory.quantity}
              onClose={() => this.setState({ isOpen: false })}
              refreshBatches={() => this.loadBatches()}
              updateInventory={this.updateInventory}
              inventoryLogStore={this.state.inventoryLogStore}
            />
          )}
        </div>
      </td>
    )
  }
}
